// Drobečková navigace kategorie v nástroji „Řazení v kategoriích":
// nadřazené kategorie jsou klikací, u aktuální šipka ▾ s podkategoriemi
// (po najetí myší / kliknutí na dotyku).

import { useState } from 'react';

export type BreadcrumbCategory = {
  guid: string;
  name: string;
  parentGuid: string | null;
  visible: boolean;
};

type Props<T extends BreadcrumbCategory> = {
  categories: T[];
  selected: T;
  onSelect: (cat: T) => void;
};

export function CategoryBreadcrumb<T extends BreadcrumbCategory>({
  categories,
  selected,
  onSelect,
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const byGuid = new Map(categories.map((c) => [c.guid, c]));

  const chain: T[] = [];
  let cur: T | undefined = selected;
  for (let i = 0; cur && i < 10; i++) {
    chain.unshift(cur);
    cur = cur.parentGuid ? byGuid.get(cur.parentGuid) : undefined;
  }
  const children = categories.filter((c) => c.parentGuid === selected.guid);

  return (
    <div className="flex items-center flex-wrap gap-x-1.5 text-lg font-semibold text-text">
      {chain.map((c, i) => {
        const last = i === chain.length - 1;
        return (
          <span key={c.guid} className="flex items-center gap-1.5">
            {i > 0 && <span className="text-text-muted font-normal">›</span>}
            {last ? (
              <span
                className="relative flex items-center gap-1"
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
              >
                <span>{c.name}</span>
                {children.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setOpen((o) => !o)}
                      aria-label="Podkategorie"
                      className="text-primary text-base px-1 rounded hover:bg-bg cursor-pointer"
                    >
                      ▾
                    </button>
                    {open && (
                      <div className="absolute z-20 top-full left-0 pt-1">
                        <div className="bg-surface border border-border rounded-lg shadow-card-hover py-1 min-w-[220px] max-h-80 overflow-y-auto">
                          <div className="px-3 py-1 text-[11px] font-semibold text-text-muted uppercase tracking-wide">
                            Podkategorie
                          </div>
                          {children.map((ch) => (
                            <button
                              key={ch.guid}
                              type="button"
                              onClick={() => {
                                setOpen(false);
                                onSelect(ch);
                              }}
                              className="w-full text-left px-3 py-1.5 text-sm font-normal text-text hover:bg-bg cursor-pointer"
                            >
                              {ch.name}
                              {!ch.visible && (
                                <span className="ml-1.5 text-xs text-text-muted">(skrytá)</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onSelect(c)}
                className="text-primary hover:underline cursor-pointer"
              >
                {c.name}
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}
